  // Function to format RUT
  import { jwtDecode } from "jwt-decode";
  import moment from 'moment';

  export const formatRut = (rut) => {
    // Remove any non-digit characters (except 'K' and 'k')
    let cleanRut = rut.replace(/[^0-9kK]+/g, '').toUpperCase();
    // Add the hyphen if necessary
    if (cleanRut.length > 1) {
      cleanRut = cleanRut.slice(0, -1) + '-' + cleanRut.slice(-1);
    }
    return cleanRut;
  };

  // Function to validate RUT
  export const validateRut = (rut) => {
    // Regular expression for RUT format (e.g., 12345678-9)
    const pattern = /^(\d{1,8})-(\d|K)$/;

    if (!pattern.test(rut)) return false;

    const [body, dv] = rut.split('-');
    let suma = 0;
    let multiplo = 2;

    for (let i = body.length - 1; i >= 0; i--) {
      suma += multiplo * body[i];
      multiplo = multiplo === 7 ? 2 : multiplo + 1;
    }

    let dvCalc = 11 - (suma % 11);
    if (dvCalc === 11) dvCalc = '0';
    if (dvCalc === 10) dvCalc = 'K';

    return String(dvCalc).toUpperCase() === dv.toUpperCase();
  };

  export const splitSlug = (str) => {
    const slug = str.substring(0, 16);
    const id = str.substring(16);
  
    return { slug, id };
  };

  export const randomString = (length) => {
    let randomString = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < length; i++) {
      randomString += characters.charAt(Math.floor(Math.random() * characters.length));
    }

    return randomString;
  };

  // Boolean function to validate 24-hour time format (HH:mm). Example: 00:00, 14:30, 23:59, 24:00, etc
  // Does not acept format like: 30:30, 28:00, 24:60, etc
  export const validate24TimeFormat = (time) => {
      // Regular expression for 24-hour time format (HH:mm)
      const regex = /^(?:[01]\d|2[0-3]):(?:[0-5]\d)$|^24:00$/;
      return regex.test(time);
  }

  export const formatCurrencyCLP = (amount) => {
    return new Intl.NumberFormat("es-CL", { currency: "CLP", style: "currency" }).format(amount);
  };

  export const resizeImage = (file, maxWidth, maxHeight) => {
    return optimizeImageForUpload(file, { maxDimension: Math.max(maxWidth, maxHeight) });
  };

  /**
   * Compresses and resizes an image to stay under a byte size target.
   *
   * Strategy:
   *   1. Scale down so the longest side is at most `maxDimension` px.
   *   2. Export as JPEG, stepping quality down until blob.size <= maxBytes.
   *   3. If quality hits the minimum and the file is still too big, shrink
   *      canvas by 15% and retry from step 2 (up to `maxScaleAttempts`).
   *
   * @param {File|Blob} file
   * @param {{ maxDimension?: number, maxBytes?: number }} options
   * @returns {Promise<File>}
   */
  export const optimizeImageForUpload = (file, options = {}) => {
    const {
      maxDimension = 1280,
      maxBytes = 900 * 1024,
    } = options;

    const QUALITY_STEPS = [0.85, 0.75, 0.65, 0.55, 0.45];
    const SCALE_FACTOR = 0.85;
    const MAX_SCALE_ATTEMPTS = 4;

    const toBlob = (canvas, quality) =>
      new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed'))),
          'image/jpeg',
          quality
        );
      });

    const compress = async (canvas, scaleAttempt) => {
      for (const quality of QUALITY_STEPS) {
        const blob = await toBlob(canvas, quality);
        if (blob.size <= maxBytes) return blob;
      }

      if (scaleAttempt >= MAX_SCALE_ATTEMPTS) {
        return toBlob(canvas, QUALITY_STEPS[QUALITY_STEPS.length - 1]);
      }

      const scaled = document.createElement('canvas');
      scaled.width = Math.round(canvas.width * SCALE_FACTOR);
      scaled.height = Math.round(canvas.height * SCALE_FACTOR);
      scaled.getContext('2d').drawImage(canvas, 0, 0, scaled.width, scaled.height);
      return compress(scaled, scaleAttempt + 1);
    };

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('FileReader error'));
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error('Image loading error'));
        img.onload = async () => {
          try {
            let { width, height } = img;
            const longest = Math.max(width, height);
            if (longest > maxDimension) {
              const ratio = maxDimension / longest;
              width = Math.round(width * ratio);
              height = Math.round(height * ratio);
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);

            const blob = await compress(canvas, 0);
            const baseName = file.name.replace(/\.[^.]+$/, '');
            resolve(new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' }));
          } catch (err) {
            reject(err);
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };
  
  export const isTokenExpired = () => {
    // Retrieve the token from localStorage
    const token = localStorage.getItem('userToken');
    if (!token) {
        return true;
    }

    // Attempt to get the expiration date from localStorage
    const expToken = localStorage.getItem('expToken');
    const storedTokenHash = localStorage.getItem('tokenHash');
    const currentTokenHash = btoa(token); // Base64 encode the current token to create a simple hash

    // Check if the stored expiration corresponds to the current token
    if (!expToken || storedTokenHash !== currentTokenHash) {
        try {
            // Decode the token
            const decoded = jwtDecode(token);
            const expirationDate = new Date(decoded.exp * 1000);

            // Store the new expiration date and token hash
            localStorage.setItem('expToken', decoded.exp * 1000);
            localStorage.setItem('tokenHash', currentTokenHash);

            // Determine if the token has expired
            const isExpired = expirationDate < new Date();
            if (isExpired) {
                localStorage.clear(); // Clear localStorage if the token is expired
                // redirect to admin_login
                window.location.href = "/admin_login";
            } else {
            }
            return isExpired;
        } catch (error) {
            console.error("Failed to decode token:", error);
            return true;
        }
    } else {
        // Use the existing expiration date
        const expirationDate = new Date(parseInt(expToken, 10));

        // Determine if the token has expired
        const isExpired = expirationDate < new Date();
        if (isExpired) {
            localStorage.clear(); // Clear localStorage if the token is expired
        } else {
        }
        return isExpired;
    }
  };


  export const mapFrontendToBackend = (category) => {
    const mapping = {
      '1ra': 'M1ra', '2da': 'M2da', '3ra': 'M3ra', '4ta': 'M4ta',
      '5ta': 'M5ta', '6ta': 'M6ta', 'A': 'FA', 'B': 'FB', 'C': 'FC', 'D': 'FD', 'Sin categoría' : 'Sin categoría'
    };
    return mapping[category] || category;
  };

  export const mapBackendToFrontend = (category) => {
    const mapping = {
      'M1ra': '1ra', 'M2da': '2da', 'M3ra': '3ra', 'M4ta': '4ta',
      'M5ta': '5ta', 'M6ta': '6ta', 'FA': 'A', 'FB': 'B', 'FC': 'C', 'FD': 'D'
    };
    return category ? (mapping[category] || 'Sin categoría') : 'Sin categoría';
  };
  
  export const mapBackendToFrontendUser = (category) => {
    const mapping = {
        'Primera masculino': '1ra', 'Segunda masculino': '2da', 'Tercera masculino': '3ra', 'Cuarta masculino': '4ta', 'Quinta masculino': '5ta', 'Sexta masculino': '6ta', 
        'Primera femenino': 'A', 'Segunda femenino': 'B', 'Tercera femenino': 'C', 'Cuarta femenino': 'D'
    };
    return category ? (mapping[category] || 'Sin categoría') : 'Sin categoría';
  };


  // React Datepicker functions

  const days = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa']
  const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  export const locale = {
    localize: {
      day: n => days[n],
      month: n => months[n]
    },
    formatLong: {
      date: () => "dd/MM/yyyy"
    }
  }

  // Schedule functions
  
  export const calculateTotalBookings = (bookings) => bookings.length;

  export const getPaymentStatusClass = (schedule) => {
    switch (schedule?.payment_status) {
        case 'Pagado':
            return 'go-schedules-blocks-item--p100';
        case 'Abonado':
            return 'go-schedules-blocks-item--p50';
        case 'Bloqueado':
            return 'go-schedules-blocks-item--blocked';
        case 'Clases':
            return 'go-schedules-blocks-item--clases';
        case 'GoMatch':	
            return 'go-schedules-blocks-item--gomatch';
        case 'Reservado':	
            return 'go-schedules-blocks-item--reservado';
        default:
            return 'go-schedules-blocks-item--available-v2';
    }
  };

  export const getBookingStatus = (bookingStatus) => {
    switch (bookingStatus) {
        case 'p0':
            return 'GoMatch';
        case 'p50':
            return 'Abonado';
        case 'pr':
            return 'Reservado';
        case 'pc':
            return 'Clases';
        case 'pb':	
            return 'Bloqueado';
        case 'p100':	
            return 'Pagado';
        default:
            return 'GoMatch';
    }
  }

  export const scheduleStatus = (schedule) => {

    switch (schedule.payment_status) {
        case 'Pagado':
            return 'pagado';
        case 'Abonado':
            return 'abonado';
        case 'Bloqueado':
            return 'bloqueado';
        case 'Clases':
            return 'clases';
        case 'GoMatch':
            return 'gomatch';
        case 'Reservado':
              return 'Reservado';
        default:
            return 'disponible';
    }

  }

  export const isScheduleDisabled = (schedule, nextSchedule, selectedDuration, index, schedules) => {
    const lastBlockIndex = schedules.length - 1;
    const durationInBlocks = selectedDuration === 5 ? 1 : 0;

    if (index >= lastBlockIndex - durationInBlocks && !schedule.available) {
        return '';
    }

    if (index >= lastBlockIndex - durationInBlocks) {
        return 'pdl-schedules-item--disabled';
    }

    return schedule.available && nextSchedule && (nextSchedule.available === false) && schedule.start_time ? 'pdl-schedules-item--disabled' : '';
  };

  export const getHighlightClass = (schedule, index, schedules, hoveredItemId, selectedDuration) => {
    const hoveredIndex = schedules.findIndex(s => s.id === hoveredItemId);

    if (hoveredIndex === -1 || !schedules[hoveredIndex].available) return '';

    const hoveredSchedule = schedules[hoveredIndex];
    const isHoveredItemBuilding = hoveredSchedule.bookings.length !== 0 && hoveredSchedule.payment_status === null;

    const durationInBlocks = selectedDuration === 3 ? 0 : selectedDuration === 4 ? 1 : 2;
    const isHoveredItemLastOrSecondToLast = hoveredIndex >= schedules.length - durationInBlocks;

    let nextAvailableCount = 0;
    let nextAvailable = true;
    for (let i = hoveredIndex + 1; i < schedules.length; i++) {
        if (!schedules[i].available) {
            nextAvailable = false;
            break;
        }
        nextAvailableCount++;
        if (nextAvailableCount >= durationInBlocks) break;
    }

    if (!nextAvailable || isHoveredItemLastOrSecondToLast) return '';

    const highlightBuildingClass = isHoveredItemBuilding ? ' highlight-building' : '';

    if (index === hoveredIndex) return 'highlight-1' + highlightBuildingClass;

    for (let i = 1; i <= durationInBlocks; i++) {
        if (index === hoveredIndex + i) {
            return `highlight-${i + 1}` + highlightBuildingClass;
        }
    }

    return '';
  };

  export const canTriggerClick = (schedules, selectedDuration) => {
    if (!schedules) return false;

    const highlights = schedules.map(schedule => getHighlightClass(schedule, schedules.indexOf(schedule), schedules));
    
    if (selectedDuration === 3) {
        return highlights.includes('highlight-1');
    }
    
    if (selectedDuration === 4) {
        return highlights.includes('highlight-2');
    }
    
    if (selectedDuration === 5) {
        return highlights.includes('highlight-2') && highlights.includes('highlight-3');
    }
    
    return false;
};


export const mapMeasurementUnit = (unit) => {
  switch (unit) {

    case 'Kilogramos':
      return 'kg';
    case 'Kilo':
      return 'kg';
    case 'Gramos':
      return 'gr';
    case 'Litros':
      return 'lt';
    default:
      return 'kg';
  }
};

 


  


